import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ConfigInterface, ConfigParticipantCopySupportByNotificationType } from '../../interfaces/config.interface';
import { OdataConfigInstanceToken, OdataConfigInterface } from '../../interfaces/odata-config.interface';
import { ConfigModel } from '../../models/low-level/config.model';
import { EventService } from '../event/event.service';
import emojiRegex from 'emoji-regex';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import { InitializableInterface } from '../../interfaces/initializable.interface';

@Injectable({
    providedIn: 'root',
})
export class ConfigService implements ConfigInterface, InitializableInterface {
    private models: ConfigModel[] = [];
    public isInitialized = false;
    private defaultEmojiRegex: string = ConfigConstants.defaultEmojiRegex;
    public emojisRegexString = '';
    public defaultAllowedMimeTypes: string[] = ['image/tiff', 'image/jpg', 'image/jpeg', 'application/pdf'];

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(OdataConfigInstanceToken) private readonly odataConfigService: OdataConfigInterface,
        private readonly eventService: EventService,
        private readonly localStorage: LocalStorageCommonService,
        private readonly paceInsights: PaceInsightsService,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    /**
     * Initialize the config service on app-start.ts
     *
     * @returns an observable of the config models
     * @throws {Error} if service is not initialized
     */
    public init(): Observable<ConfigModel[]> {
        return this.odataConfigService.getConfig().pipe(
            tap((models) => {
                this.paceInsights.trackEvent('loaded_config', Object.fromEntries(models.map((m) => [m.key, m.value])));
                this.isInitialized = true;
                this.models = models;

                // initialize emoji regex with failsafe
                this.setEmojisRegexString();

                // Store the backend in local storage for easier debugging
                this.localStorage.preInitializationStorageSet(
                    ConfigConstants.configKeyBackendName,
                    this.getBackendName(),
                );
            }),
        );
    }

    /**
     * Initialize emoji regex with failsafe
     * Suppressing warning about unused variable since we do not actually need the regex here, we just
     * need to ensure that the value can be parsed into a regex
     */
    private setEmojisRegexString(): void {
        const emojisRegexString =
            this.getConfigModelByKey(ConfigConstants.configKeyEmojisRegex)?.value ?? this.defaultEmojiRegex;
        try {
            // Suppressing warning about unused variable since we do not actually need the regex here, we just
            // need to ensure that the value can be parsed into a regex

            // Matches UTF-16 High and Low surrogate pairs to detect emojis (characters above U+FFFF)
            const surrogateEmojiRegex = `[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]`;
            const emojiRegexFromLibrary = emojiRegex().source;
            this.emojisRegexString = `${surrogateEmojiRegex}|${emojisRegexString}|${emojiRegexFromLibrary}`;
        } catch {
            this.log.error(`ConfigService: init: Error parsing emoji regex: '${emojisRegexString}'`);
        }
    }

    /**
     * Returns true if the config value for the given key equals the SAP true value ('X').
     *
     * @param key - The configuration key to look up
     * @param defaultValue - Fallback value when the key is not found (default: false)
     * @returns True if the config value equals ConfigConstants.sapTrue, otherwise defaultValue
     */
    private isSapTrue(key: string, defaultValue = false): boolean {
        const model = this.getConfigModelByKey(key);
        const newValue = model ? model.value === ConfigConstants.sapTrue : defaultValue;
        return newValue;
    }

    /**
     * Parses the config value for the given key as an integer.
     *
     * @param key - The configuration key to look up
     * @param defaultValue - Fallback value when the key is not found or the value is not a valid number (default: 0)
     * @param min - Optional minimum accepted value; parsed values below this threshold fall back to defaultValue
     * @returns The parsed integer, or defaultValue if the value is missing, non-numeric, or below min
     */
    private parseNumber(key: string, defaultValue = 0, min?: number): number {
        const model = this.getConfigModelByKey(key);
        const parsed = model ? parseInt(model.value, 10) : NaN;
        const newValue = !isNaN(parsed) && (typeof min === 'undefined' || parsed >= min) ? parsed : defaultValue;
        return newValue;
    }

    /**
     * Returns the raw string config value for the given key.
     *
     * @param key - The configuration key to look up
     * @param defaultValue - Fallback value when the key is not found (default: '')
     * @returns The config value string, or defaultValue if the key is not found
     */
    private getStringValue(key: string, defaultValue = ''): string {
        const model = this.getConfigModelByKey(key);
        const newValue = model ? model.value : defaultValue;
        return newValue;
    }

    /**
     * Splits the config value for the given key by comma into a string array.
     * Returns defaultValue when the key is not found or the value is an empty string.
     *
     * @param key - The configuration key to look up
     * @param defaultValue - Fallback value when the key is not found or the value is empty (default: [])
     * @param splitter
     * @returns Array of trimmed substrings split by ',', or defaultValue
     */
    private getSplittedStrings(key: string, defaultValue: readonly string[] = [], splitter = ','): string[] {
        const model = this.getConfigModelByKey(key);
        const newValue = model && model.value.length > 0 ? model.value.split(splitter) : [...defaultValue];
        return newValue;
    }

    /**
     * Checks if a configuration value includes a specific key string.
     *
     * @param key - The configuration key to look up
     * @param includeKey - The string to search for within the config value
     * @param defaultValue
     * @returns True if the config value includes the specified key, false otherwise
     * @private
     */
    private includesConfigKey(key: string, includeKey: string, defaultValue = false): boolean {
        const model = this.getConfigModelByKey(key);
        const newValue = model && includeKey.length > 0 ? model.value.includes(includeKey) : defaultValue;
        return newValue;
    }

    /**
     * Checks if a configuration value (comma-separated list) contains a specific value.
     *
     * Splits the config value by comma and space, then checks if any element
     * exactly matches the provided key.
     *
     * @param key - The configuration key to look up
     * @param someKey - The value to search for in the comma-separated list
     * @param defaultValue
     * @param splitter
     * @returns True if the value is found in the list, false otherwise
     * @private
     */
    private hasSomeValue(key: string, someKey: string, defaultValue = false, splitter = ', '): boolean {
        const model = this.getConfigModelByKey(key);
        const newValue =
            model && someKey.length > 0
                ? model.value.split(splitter).some((value) => value.toLowerCase() === someKey.toLowerCase())
                : defaultValue;
        return newValue;
    }

    /**
     * Common method to get the ConfigModel value using Key
     *
     * @param key The key of the ConfigModel
     * @returns The ConfigModel or undefined if not found
     * @throws {Error} if service is not initialized
     */
    private getConfigModelByKey(key: string): ConfigModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getConfigModelByKey: Trying to call ConfigService before initialization for key '${key}'!`,
            );
        }
        return this.models.find((model) => model.key === key);
    }

    /* ----------------------------- Custom getters ------------------------------------------------------------ */

    public getMaterialErrorCodeCountMax(): number {
        return this.parseNumber(ConfigConstants.configKeyMaterialErrorCodeCountMax, Infinity);
    }

    public getCseEmailFax(): string {
        return this.getStringValue(ConfigConstants.configKeyCseEmailFax);
    }

    public getShowRefreshXMLButton(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowRefreshXMLButton);
    }

    public getDisplayCustomerContact(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCustomerContact);
    }

    public getShowLanguageSelectionDropdown(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowLanguageSelectionDropdown);
    }

    public getEscalationEmail(): string {
        return this.getStringValue(ConfigConstants.configKeyEscalationEmail);
    }

    public getInstructionsUrl(): string {
        return this.getStringValue(ConfigConstants.configKeyInstructionsUrl);
    }

    public getShowDocumentationLink(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowDocumentationLink);
    }

    public getUserFrontId(): string {
        return this.getStringValue(ConfigConstants.configKeyUserFrontId);
    }

    public getMaxInterimsCount(): number {
        return this.parseNumber(ConfigConstants.configKeyMaxInterimsCount);
    }

    public getComplaintNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyComplaintNotifTypes, notificationType);
    }

    /**
     * Get the CSE Currency string
     *
     * @returns the CSE Currency string
     * @throws {Error} if service is not initialized
     */
    public getExpenseCurrency(): string {
        return this.getStringValue(ConfigConstants.configKeyExpenseCurrency);
    }

    /**
     * Gets the CSE Country
     *
     * @returns the CSE Country
     * @throws {Error} if service is not initialized
     */
    public getCseCountry(): string {
        return this.getStringValue(ConfigConstants.configKeyCseCountry);
    }

    public getShowInfoToCCC(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowInfoToCCC);
    }

    /**
     * Gets the LegalCheckIEC62353Text to display for equipment checkbox
     *
     * @returns the LegalCheckIEC62353Text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Text(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalIEC62353Text);
    }

    /**
     * Gets the LegalCheckIEC62353Code to compare with Equipment.Division
     *
     * @returns the LegalCheckIEC62353Code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Code(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalIEC62353Code);
    }

    /**
     * Gets the LegalCheckPCResistanceText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckPCResistanceText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalPCResistanceText);
    }

    /**
     * Gets the LegalCheckPCResistanceCode to compare with Equipment.Division
     *
     * @returns the LegalCheckPCResistanceCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalPCResistanceCode);
    }

    /**
     * Gets the LegalCheckDeviceLeakageText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckDeviceLeakageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalDeviceLeakageText);
    }

    /**
     * Gets the LegalCheckDeviceLeakageCode to compare with Equipment.Division
     *
     * @returns the LegalCheckDeviceLeakageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalDeviceLeakageCode);
    }

    /**
     * Gets the LegalCheckXRayText to display for equipment checkbox only for Country-Germany
     *
     * @returns the LegalCheckXRayText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalXRayText);
    }

    /**
     * Gets the LegalCheckXRayCode to compare with Equipment.Division only for Country-Germany
     *
     * @returns the LegalCheckXRayCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalXRayCode);
    }

    /**
     * Gets the LegalCheckRadioProtectionText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalRadioProtectionText);
    }

    /**
     * Gets the LegalCheckRadioProtectionCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalRadioProtectionCode);
    }

    /**
     * Gets the LegalCheckInstallationCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalInstallationCheckText);
    }

    /**
     * Gets the LegalCheckInstallationCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalInstallationCheckCode);
    }

    /**
     * Gets the LegalCheckStatusCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalStatusCheckText);
    }

    /**
     * Gets the LegalCheckStatusCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalStatusCheckCode);
    }

    /**
     * Gets the LegalCheckConsistencyCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckConsistencyCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalConsistencyCheckText);
    }

    /**
     * Gets the LegalCheckConsistencyCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check consistency check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalConsistencyCheckCode);
    }

    /**
     * Gets the LegalCheckPartiallyCheckedText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partially checked text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalPartiallyCheckedText);
    }

    /**
     * Gets the LegalCheckPartiallyCheckedCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check partially checked code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalPartiallyCheckedCode);
    }

    /**
     * Gets the getLegalCheckAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckAcceptanceTestCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalAcceptanceTestCode);
    }

    /**
     * Gets the getLegalCheckPartialAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partial acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartialAcceptanceTestCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalPartialAcceptanceTestCode);
    }

    /**
     * Gets the getLegalCheckHealthCheckCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check health check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckHealthCheckCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalHealthCheckCode);
    }

    /**
     * Gets the getLegalCheckConsistencyTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check consistency test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyTestCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalConsistencyTestCode);
    }

    /**
     * Gets the LegalCheckNEN1010Text to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check NEN1010 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Text(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalNEN1010Text);
    }

    /**
     * Gets the LegalCheckNEN1010Code to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check NEN1010 code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Code(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalNEN1010Code);
    }

    /**
     * Gets the LegalCheckK3RoomText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check K3 room text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalK3RoomText);
    }

    /**
     * Gets the LegalCheckK3RoomCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check K3 room code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalK3RoomCode);
    }

    /**
     * Gets the LegalCheckMeasurementPerformedText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement performed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalMeasurementPerformedText);
    }

    /**
     * Gets the LegalCheckMeasurementPerformedCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement performed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalMeasurementPerformedCode);
    }

    /**
     * Gets the LegalCheckInsulationResistanceText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check insulation resistance text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalInsulationResistanceText);
    }

    /**
     * Gets the LegalCheckInsulationResistanceCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check insulation resistance code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalInsulationResistanceCode);
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement not needed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalMeasurementNotNeededText);
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement not needed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalMeasurementNotNeededCode);
    }

    /**
     * Break time between end-of-work and start-of-work.
     *
     * @returns the break time in hours
     * @throws {Error} if service is not initialized
     */
    public getEowSowBreakHours(): number {
        return this.parseNumber(ConfigConstants.configKeyEowSowBreakHours);
    }

    /**
     * Time in hours that end-of-work can be moved into the future
     *
     * @returns the flexible hours for end-of-work
     * @throws {Error} if service is not initialized
     */
    public getEowFlexibleHours(): number {
        return this.parseNumber(ConfigConstants.configKeyEowFlexibleHours, ConfigConstants.eowFlexibleHours);
    }

    /**
     * Determines the display of equipment based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowEquipment' has value 'CSE,APPS' , then return true
     *
     * @param taskGroup the task group of the notification
     * @returns true if the equipment should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showEquipment(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowEquipment, taskGroup, true);
    }

    /**
     * Determines the display of material based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowMaterialConsumption' has value 'CSE' , then return false
     *
     * @param taskGroup the task group of the notification
     * @returns true if the material should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMaterialConsumption(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowMaterialConsumption, taskGroup, true);
    }

    /**
     * Determines the display of MeasurementTools based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measurement tools should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasurementTools(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowMeasurementTools, taskGroup, true);
    }

    /**
     * Determines the display of ParticipantList based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the participant list should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showParticipantList(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowParticipantList, taskGroup);
    }

    /**
     * Determines the display of Follow-Up based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the follow-up should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showFollowup(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowFollowup, taskGroup);
    }

    public showFollowupLongText(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowFollowupLongText);
    }

    public showFollowupAdditionalInfo(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowFollowupAdditionalInfo);
    }

    /**
     * Determines the display of Measuring Points based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measuring points should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasuringPoints(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowMeasuringPoints, taskGroup);
    }

    public getFollowupDefaultAdditionalTime(): number {
        return this.parseNumber(ConfigConstants.configKeyFollowupDefaultAdditionalTime, Infinity);
    }

    public getFollowupMinAdditionalTime(): number {
        return this.parseNumber(ConfigConstants.configKeyFollowupMinAdditionalTime, Infinity);
    }

    public getFollowupAdditionalTimeStep(): number {
        return this.parseNumber(ConfigConstants.configKeyFollowupAdditionalTimeStep, Infinity);
    }

    public getAppsTaskCode(): string {
        return this.getStringValue(ConfigConstants.configKeyAppsTaskCode);
    }

    public getStatusAfterFixValueForQCFlag(): string {
        return this.getStringValue(ConfigConstants.configKeyStatusAfterFixValueForQCFlag);
    }

    /**
     * get the ETA limit from config service
     *
     * @returns the ETA limit in hours
     * @throws {Error} if service is not initialized
     */
    public getEtaLimitMinutes(): number {
        return this.parseNumber(ConfigConstants.configKeyEtaLimitMinutes, ConfigConstants.defaultEtaLimitMinutes);
    }

    public getMaterialHistoryLimit(): number {
        return this.parseNumber(ConfigConstants.configKeyMaterialHistoryLimit, Infinity);
    }

    /**
     * Configuration to determine how many days back in time
     * is the limit to retrieve notifications
     *
     * @example 30 retrieve notifications for the last 30 days
     * Used for DocBox side menu to display notifications history list
     *
     * @returns number representing the limit of days i.e.: 30, 60, 90
     * @throws {Error} if service is not initialized
     */
    public getNotificationsHistoryDaysLimit(): number {
        return this.parseNumber(ConfigConstants.configKeyNotificationHistoryDays, 30);
    }

    /**
     * get the headquarter detail line 1 from config service
     *
     * @returns the headquarter detail line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ1(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ1);
    }

    /**
     * get the headquarter detail line 2 from config service
     *
     * @returns the headquarter detail line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ2(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ2);
    }

    /**
     * get the headquarter detail line 3 from config service
     *
     * @returns the headquarter detail line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ3(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ3);
    }

    /**
     * get the headquarter detail line 4 from config service
     *
     * @returns the headquarter detail line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ4(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ4);
    }

    /**
     * get the headquarter detail link label from config service
     *
     * @returns the headquarter detail link label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ5);
    }

    /**
     * get the headquarter detail link from config service
     *
     * @returns the headquarter detail link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5Link(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQ5Link);
    }

    /**
     * get the headquarter detail telephone number details from config service
     *
     * @returns the headquarter detail telephone number details
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQTel(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoHQTel);
    }

    /**
     * get the Support hours for corporate Information for NGMAA
     *
     * @returns the Support hours for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportHours(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoSupportHours);
    }

    /**
     * get the Support Email for corporate Information for NGMAA
     *
     * @returns the Support Email for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportEmail(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoSupportEmail);
    }

    /**
     * get the Support link for corporate Information for NGMAA
     *
     * @returns the Support link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportLink(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoSupportLink);
    }

    /**
     * get the Support create ticket link for corporate Information for NGMAA
     *
     * @returns the Support create ticket link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportCreateTicketLink(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoSupportCreateTicketLink);
    }

    /**
     * get the legal info line 1 from config service
     *
     * @returns the legal info line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo1(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo1);
    }

    /**
     * get the legal info line 2 from config service
     *
     * @returns the legal info line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo2(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo2);
    }

    /**
     * get the legal info line 3 from config service
     *
     * @returns the legal info line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo3(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo3);
    }

    /**
     * get the legal info line 4 from config service
     *
     * @returns the legal info line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo4(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo4);
    }

    /**
     * get the legal info line 5 from config service
     *
     * @returns the legal info line 5
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo5(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo5);
    }

    /**
     * get the legal info line 6 from config service
     *
     * @returns the legal info line 6
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo6(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo6);
    }

    /**
     * get the legal info line 7 from config service
     *
     * @returns the legal info line 7
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo7(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfo7);
    }

    /**
     * get the legal info policy Label from config service
     *
     * @returns the legal info policy Label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicy(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfoPolicy);
    }

    /**
     * get the legal info policy link from config service
     *
     * @returns the legal info policy link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicyLink(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfoPolicyLink);
    }

    /**
     * get the legal info TOU label from config service
     *
     * @returns the legal info TOU label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOU(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfoTOU);
    }

    /**
     * get the legal info TOU link from config service
     *
     * @returns the legal info TOU link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOULink(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalInfoTOULink);
    }

    /**
     * get the legal info policy month from config service
     *
     * @returns the legal info policy month
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalPolicyMonth(): string {
        return this.getStringValue(ConfigConstants.configKeyCorpInfoLegalPolicyMonth);
    }

    /**
     * get the global storage location from config service
     *
     * @returns the global storage location
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocation(): string[] {
        return this.getSplittedStrings(ConfigConstants.configKeyGlobalStorageLocation);
    }

    /**
     * get the global storage location for Material Pricing screen
     *
     * @returns the global storage location for Material Pricing screen
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocationMatPricing(): string[] {
        return this.getSplittedStrings(ConfigConstants.configKeyGlobalStorageLocationMatPricing);
    }

    public getGlobalStorageLocationMatRequest(): string {
        return this.getStringValue(ConfigConstants.configKeyGlobalStorageLocationMatRequest);
    }

    /**
     * get the Commercial Info default text from config service
     *
     * @returns the Commercial Info default text
     * @throws {Error} if service is not initialized
     */
    public getCIDefaultText(): string {
        return this.getStringValue(ConfigConstants.configKeyCIDefaultText);
    }

    /**
     * get the Previous days limit the timesheet can be filled up
     *
     * @returns the Previous days limit the timesheet can be filled up
     * @throws {Error} if service is not initialized
     */
    public getPreviousDaysTimesheetLimit(): number {
        return this.parseNumber(ConfigConstants.configKeyPreviousDaysTimesheetLimit);
    }

    /**
     * get the maximum allowed future time(in minutes) the timesheet can be submitted
     *
     * @returns the maximum allowed future time(in minutes) the timesheet can be submitted
     * @throws {Error} if service is not initialized
     */
    public getMaxAllowedFutureTime(): number {
        return this.parseNumber(ConfigConstants.configKeyMaxAllowedFutureTime);
    }

    /**
     * get the default followup time for NGMP58
     *
     * @returns the default followup time for NGMP58
     * @throws {Error} if service is not initialized
     */
    public getDefaultFollowUpTimeP58(): string {
        return this.getStringValue(ConfigConstants.configKeyFollowupDefaultTimeP58, '0');
    }

    /**
     * get the flag to switch on using the endpoint for customised forms
     *
     * @returns the flag to switch on using the endpoint for customised forms
     * @throws {Error} if service is not initialized
     */
    public getUseCustomisedForms(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseCustomisedForms);
    }

    /**
     * Get the Legal requirement checks for Germany in-vitro
     *             --Text--                       --Code--
     * Checkbox Checked according VDE0701/0702      VDE1
     *       Protective Conductor Resistant in mOhm VDE2
     *       Equivalent Device leakage in mA        VDE3
     *       Insulation Resistant in MOhm           VDE4
     *
     * @returns the legal check VDE0701/0702 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckVDE701702Text(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDE701702Text);
    }

    public getLegalCheckVDE701702Code(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDE701702Code);
    }

    public getLegalCheckVDEPCResistanceText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEPCResistanceText);
    }

    public getLegalCheckVDEPCResistanceCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEPCResistanceCode);
    }

    public getLegalCheckVDEDeviceLeakageText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEDeviceLeakageText);
    }

    public getLegalCheckVDEDeviceLeakageCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEDeviceLeakageCode);
    }

    public getLegalCheckVDEInsulationResistanceText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEInsulationResistanceText);
    }

    public getLegalCheckVDEInsulationResistanceCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVDEInsulationResistanceCode);
    }

    // get the flag to switch on remove 9999 from error code
    public getUseNoCustomErrorCode(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseNoCustomErrorCode);
    }

    // get the flag to switch on Add Material Return
    public getUseAddNewMaterialReturn(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseAddNewMaterialReturn);
    }

    // get the report text length as defined for Sales org / Plant
    public getReportTextLineLength(): number {
        return this.parseNumber(ConfigConstants.configKeyReportTextLineLength, ConfigConstants.defaultLineLength);
    }

    // get the report text max num of allowed lines as defined for Sales org / Plant
    public getReportTextMaxLines(): number {
        return this.parseNumber(ConfigConstants.configKeyReportTextMaxLines, Infinity);
    }

    public getMaxRecipientEntries(): number {
        return this.parseNumber(ConfigConstants.configKeyMaxRecipientEntries, ConfigConstants.maxRecipientEntries);
    }

    public getSelectDefaultFollowupReason(): boolean {
        return this.isSapTrue(ConfigConstants.configKeySelectDefaultFollowupReason, true);
    }

    public getEnableSafetyAssessmentForm(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableSafetyAssessmentForm);
    }

    public getBootstockReplenishEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyBootstockReplenishEnabled);
    }

    public getSafetyAssessmentForm(): string {
        return this.getStringValue(ConfigConstants.configKeySafetyAssessmentForm);
    }

    public getEnablePaceFormsCrossDeviceSync(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnablePaceFormsCrossDeviceSync);
    }

    public getDisplayAdditionalMaterialRequestInformation(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAdditionalMatReqInfo);
    }

    public getAddMaterialCurrencyCode(): string {
        return this.getStringValue(ConfigConstants.configKeyAddMaterialCurrencyCode);
    }

    public getAddMaterialSelectBillableDefault(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAddMaterialSelectBillableDefault);
    }

    public getAddMaterialBillingIndicator(): string {
        return this.getStringValue(ConfigConstants.configKeyAddMaterialBillingIndicator);
    }

    public getAddMaterialBillingChangeable(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAddMaterialBillingChangeable);
    }

    public getShowCommercialInfo(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowCommercialInfo, true);
    }

    /**
     * Gets the CSML Tracker Link to track the material Ordered
     *
     * @returns the CSML Tracker Link
     * @throws {Error} if service is not initialized
     */
    public getCSMLTrackerLink(): string {
        return this.getStringValue(ConfigConstants.configKeyCSMLTrackerLink);
    }

    public getAddDeliveryAddressTaxi(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAddDeliveryAddressTaxi, true);
    }

    public getDisablePaceForms(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisablePaceForms);
    }

    public getDisplayNonProductiveHours(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayNonProductiveHours, true);
    }

    public getDisplayAdditionalTravelBack(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAdditionalTravelBack);
    }

    public getDisplayTravelBackSummary(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayTravelBackSummary);
    }

    public getDisplayAdditionalTravelInfo(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAdditionalTravelInfo);
    }

    public getDisplayMeansOfTransport(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayMeansOfTransport);
    }

    public getDisplayAdditionalMaterialReturnInformation(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAdditionalMatReturnInfo);
    }

    public getFormatMultilineTextPattern(): string {
        return this.getStringValue(ConfigConstants.configKeyFormatMultilineTextPattern);
    }

    public getFormatInternalInfoMultilineTextPattern(): string {
        return this.getStringValue(ConfigConstants.configKeyFormatInternalInfoMultilineTextPattern);
    }

    public getDivisionsWithAdditionalLegalText(division: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDivisionsWithAdditionalLegalText, division);
    }

    public getDivisionsWithIEC62353(division: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDivisionsWithIEC62353, division);
    }

    public getDivisionsWithVDE701702(division: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDivisionsWithVDE701702, division);
    }

    /**
     * Get the form types for which the participant copy workflow should be enabled for a given notification type.
     * Returns a string array instead of FormsConstants.FORMTYPES
     * a) to avoid a cyclic dependency
     * b) since the values from GW are given as strings anyway, so it does not matter where we cast them
     *
     * @param notificationType The notification type for which to get the supported form types
     * @returns string[] A list of form types that should support the participant copy workflow for this notification type
     * @throws {Error} if service is not initialized
     */
    public getParticipantCopySupportByNotificationType(notificationType: ConfigConstants.NotificationTypes): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getParticipantCopySupportByNotificationType: Trying to call ConfigService before initialization!`,
            );
        }

        try {
            const formTypes = this.getConfigModelByKey(
                ConfigConstants.configKeyPaceFormsTypesForParticipantCopy,
            )?.value;
            if (formTypes) {
                const support = JSON.parse(formTypes) as ConfigParticipantCopySupportByNotificationType;
                return support[notificationType] ?? [];
            }
        } catch (error) {
            this.log.error(
                `ConfigService: getParticipantCopySupportByNotificationType: error while parsing values for key '${
                    ConfigConstants.configKeyPaceFormsTypesForParticipantCopy
                }': ${JSON.stringify(error)}`,
            );
        }

        return [];
    }

    /**
     * Get the config to determine if moment need to be created in UTC or no
     *
     * @returns true if moment need to be created in UTC
     * @throws {Error} if service is not initialized
     */
    public getFormatMomentInUTC(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyFormatMomentInUTC, true);
    }

    public getDisplayTimesheetAddress(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayTimesheetAddress);
    }

    public getTravelBackStartEditable(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyTravelBackStartEditable);
    }

    public getTravelBackEndEditable(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyTravelBackEndEditable, true);
    }

    public getTimesheetEndTimeLimit(): string {
        return this.getStringValue(ConfigConstants.configKeyTimesheetEndTimeLimit);
    }

    public getDisplayTimesheetAddressOptions(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayTimesheetAddressOptions);
    }

    public getTimesheetAddressHome(): string {
        return this.getStringValue(ConfigConstants.configKeyTimesheetAddressHome);
    }

    public getTimesheetAddressOffice(): string {
        return this.getStringValue(ConfigConstants.configKeyTimesheetAddressOffice);
    }

    public getMatPrioritiesForDOP(): string {
        return this.getStringValue(ConfigConstants.configKeyMatPrioritiesForDOP);
    }

    public getMatPrioritiesForCustomer(): string {
        return this.getStringValue(ConfigConstants.configKeyMatPrioritiesForCustomer);
    }

    public getDisplayMaterialQuote(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayMaterialQuote);
    }

    public getDisplayCustomerSignatureInForm(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCustomerSignatureInForm);
    }

    public getTimesheetPauseCodes(taskType: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyTimesheetPauseCodes, taskType);
    }

    public getSparePartsCatalogLink(): string {
        return this.getStringValue(ConfigConstants.configKeySparePartsCatalogLink);
    }

    public getDisableQuoteForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDisableQuoteForNotificationTypes, notificationType);
    }

    public getEscalateNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyEscalateNotificationTypes, notificationType, true);
    }

    public getDefaultReasonCodeForCentralScrap(): string {
        return this.getStringValue(ConfigConstants.configKeyDefaultReasonCodeForCentralScrap);
    }

    public getDisableSpecialTransportAWBfield(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisableSpecialTransportAWBfield);
    }

    public getSpecialDangerousGoodsInd(dangerousGoodInd: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeySpecialDangerousGoodsInd, dangerousGoodInd);
    }

    public getDangerousGoodsIndicator(dangerousGoodInd: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDangerousGoodsInd, dangerousGoodInd);
    }

    public getFollowUpDefaultDays(): number {
        return this.parseNumber(ConfigConstants.configKeyFollowUpDefaultDays, 1);
    }

    /**
     * Determines whether to show Planned ETA alert for the notifications based on the value from the config set
     *
     * @returns true if the alert should be shown
     * @throws {Error} if service is not initialized
     */
    public getDisplayEtaAlert(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayEtaAlert);
    }

    /**
     * get the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     *
     * @returns the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     * @throws {Error} if service is not initialized
     */
    public getEtaAlertTimerLimit(): number {
        return this.parseNumber(ConfigConstants.configKeyEtaAlertTimerLimit, ConfigConstants.defaultEtaAlertTimerLimit);
    }

    public getTextClausePattern(): string {
        return this.getStringValue(ConfigConstants.configKeyTextClausePattern);
    }

    public getShowMobileNumberForParticipants(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowMobileNumberForParticipants);
    }

    public getShowCalibrationDate(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowCalibrationDate);
    }

    public getUseCustomTimeInput(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseCustomTimeInput);
    }

    public getShowCustomerInformationOnSummary(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowCustomerInformationOnSummary);
    }

    public getFormatMultilineTextOnBlur(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyFormatMultilineTextOnBlur);
    }

    public getShowFormCustomerEmailData(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowFormCustomerEmailData);
    }

    public getUseCustomDurationStepper(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseCustomDurationStepper);
    }

    public getPrefillTroubleText(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyPrefillTroubleText, true);
    }

    public getEnableJobOnFly(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableJobOnFly);
    }

    public getJobOnFlyDefaultDuration(): number {
        return this.parseNumber(
            ConfigConstants.configKeyJobOnFlyDefaultDuration,
            ConfigConstants.jobOnFlyDefaultDuration,
        );
    }

    public getDisplayMaterialPricing(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayMaterialPricing);
    }

    public getActivateCosima(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyActivateCosima);
    }

    public getDisplayBatchNumber(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayBatchNumber);
    }

    public getDisplayWeeklyWorkTime(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayWeeklyWorkTime);
    }

    public getEnableStandbyTravel(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableStandbyTravel);
    }

    public getIsTravelBillable(orderType: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyIsTravelBillable, orderType);
    }

    public getDisplayCentralScrap(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCentralScrap, true);
    }

    public getDisplayCentralScrapForDangerousGoods(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCentralScrapDangerGoods);
    }

    public getSmartSensorTracker(): string[] {
        return this.getSplittedStrings(ConfigConstants.configKeySmartSensorTracker, ['']);
    }

    public getSmartSensorTrackerUrl(): string {
        return this.getStringValue(ConfigConstants.configKeySmartSensorTrackerUrl);
    }

    public getMatSerTypeaheadToggle(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyMatSerTypeaheadToggle);
    }

    public getMatLTextDisplayLines(): string[] {
        return this.getSplittedStrings(ConfigConstants.configKeyMatLTextDisplayLines);
    }

    public getDisplayAdditionalTravelCost(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAdditionalTravelCost);
    }

    public getAdditionalTravelCostText(): string {
        return this.getStringValue(ConfigConstants.configKeyAdditionalTravelCostText);
    }

    public getMaxWeeklyWorkingHours(): number {
        return this.parseNumber(ConfigConstants.configKeyMaxWeeklyWorkingHours, 50);
    }

    public getDisplayCseSignature(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCseSignature);
    }

    public getDisplayBootStockIndicator(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayBootStockIndicator);
    }

    public getEnableBootstockScrap(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyBootStockScrapEnabled);
    }

    public getAllowNotificationPause(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAllowNotificationPause);
    }

    public getDisplayTrendCodeInFollowup(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayTrendCodeInFollowup);
    }

    public getShowMeasurementToolsDetails(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowMeasurementToolsDetails);
    }

    public getHideParticipantFormSubmission(): boolean {
        return this.isSapTrue(ConfigConstants.configHideParticipantFormSubmission);
    }

    public getShowDataMediumInEquipment(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowDataMediumInEquipment);
    }

    public getAnaLink(): string {
        return this.getStringValue(ConfigConstants.configKeyAnaLink);
    }

    public getShowIconForMaterialDelivery(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowIconForMaterialDelivery);
    }

    public getTeamplayFleetLink(): string {
        return this.getStringValue(ConfigConstants.configKeyTeamplayFleetLink);
    }

    public getComplaintsQuestionnaireLink(appID = ''): string {
        return this.getStringValue(ConfigConstants.configKeyComplaintsQuestionnaireLink + appID);
    }

    public getSparePartCatalogMatLink(): string {
        return this.getStringValue(ConfigConstants.configKeySparePartCatalogMatLink);
    }

    public getEnableTimesheetEmail(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableTimesheetEmail);
    }

    public isMaterialLiBatteryStandAlone(unNumber = ''): boolean {
        return this.hasSomeValue(ConfigConstants.configKeyMaterialLiBatteryStandAlone, unNumber);
    }

    public isMaterialLiBatteryInDevice(unNumber = ''): boolean {
        return this.hasSomeValue(ConfigConstants.configKeyMaterialLiBatteryInDevice, unNumber);
    }

    public isTimesheetDisabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisableTimesheet);
    }

    public getDataManagementEmail(): string {
        return this.getStringValue(ConfigConstants.configKeyDataManagementEmail);
    }

    public getDataManagementEmailInvitro(): string {
        const defaultValue = this.getDataManagementEmail();
        return this.getStringValue(ConfigConstants.configKeyDataManagementEmailInvitro, defaultValue);
    }

    public getShowFleetLinkInEquipmentHistory(): boolean {
        return this.isSapTrue(ConfigConstants.showFleetLinkInEquipmentHistory);
    }

    public getShowStartImmediately(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowStartImmediately, true);
    }

    public getShowPlaceHolderForReportText(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowPlaceHolderForReportText);
    }

    public getShowPlaceHolderForEscalationText(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowPlaceHolderForEscalationText);
    }

    public getMaxWorkingHoursInTimesheet(): number {
        return this.parseNumber(ConfigConstants.configKeyMaxWorkingHoursInTimesheet, ConfigConstants.totalWorkingHours);
    }

    /**
     * @deprecated The list of userStatus types does not change and does not need to be read from gateway; therefore no
     * longer using this function.
     *
     * Model value from configSet.json is comma separated string i.e. "PSI,PPI"
     * User status type in parameter is also comma separated string i.e. "PSI,REMT"
     * After splitting  ["PSI","REMT"]
     * Iterating above ("PSI,PPI").indexOf(PSI) is 0 returns true
     *
     * @param userStatusType - user status type
     * @returns true if userStatusType is in the model value
     */
    public getHideComplaintsForUserStatusType(userStatusType: ConfigConstants.USER_STATUS): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideComplaintsForUserStatusType: Trying to call ConfigService before initialization!`,
            );
        }
        const userStatusTypeArray = userStatusType.split(/,\s*|\s+/);
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideComplaintsForUserStatusType);
        return model && userStatusTypeArray.length > 0
            ? userStatusTypeArray.some((userStatus) => userStatus.length > 0 && model.value.includes(userStatus))
            : false;
    }

    public getBackendName(): string {
        return this.getStringValue(ConfigConstants.configKeyBackendName);
    }

    public isMaterialOrderPriorityForSplit(materialOrderPriority: string): boolean {
        return this.hasSomeValue(ConfigConstants.configKeyMaterialOrderPriorityForSplit, materialOrderPriority);
    }

    public getDocboxDoctypes(): Array<{ key: string; value: string }> {
        return this.models
            .filter((model) => model.key.startsWith('DocboxDoctype_'))
            .map((model) => ({ key: model.key.toUpperCase(), value: model.value }));
    }

    public getDocBoxInstallationDocType(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocBoxInstallationDocType: Trying to call ConfigService before initialization!`,
            );
        }
        const model = this.getConfigModelByKey(ConfigConstants.configKeyDocBoxInstallationType);
        return model ? model.value : '';
    }

    public getDocBoxEnableReportTypeSelection(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocBoxInstallationDocType: Trying to call ConfigService before initialization!`,
            );
        }

        const model = this.getConfigModelByKey(ConfigConstants.configKeyDocBoxEnableReportTypeSelection);
        return model ? model.value === ConfigConstants.sapTrue : false;
    }

    public getUserCountryFI(): string {
        return this.getStringValue(ConfigConstants.userCountryFI);
    }

    public getDisclaimerForChinaFI(): string {
        const model = this.getConfigModelByKey(ConfigConstants.disclaimerForChinaFI);
        return model ? model.value.replace(/\\n/g, '\n') : '';
    }

    /**
     * SAP gateway does not support the full unicode character set. To avoid recurring problems with users entering
     * unsupported characters, we know check all text input for unsupported characters and mark them as error.
     * Since input fields can be used before the config service init was completed, we also return a default value in
     * those cases, e.g. to let users still report errors during startup of the application.
     *
     * Ideally we would also fix the naming here and on gateway to something like "getUnsupportedUnicodeRegex", but
     * what has been done has been done. Take this comment as explanation instead.
     *
     * @returns {string} - regex string to match unsupported unicode characters
     */
    public getEmojisRegex(): string {
        return this.emojisRegexString;
    }

    public getLegal3551NotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyLegal3551NotificationTypes, notificationType);
    }

    public getLegal3551DetachPowerCordText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551DetachPowerCordText);
    }

    public getLegal3551PermanentConnectedText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551PermanentConnectedText);
    }

    public getLegal3551NonDetachPowerCordText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551NonDetachPowerCordText);
    }

    public getLegal3551DetachPowerCordCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551DetachPowerCordCode);
    }

    public getLegal3551PermanentConnectedCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551PermanentConnectedCode);
    }

    public getLegal3551NonDetachPowerCordCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegal3551NonDetachPowerCordCode);
    }

    public getLegalVerificationNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyLegalVerificationNotifTypes, notificationType);
    }

    public getDivisionsWithVerificationLegalCheck(division: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyDivisionsWithVerifLegalCheck, division);
    }

    public getLegalVerifiedSecurityDeviceText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVerifiedSecurityDeviceText);
    }

    public getLegalVerifiedSecurityDeviceCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalVerifiedSecurityDeviceCode);
    }

    public getLegalResumptionAfterUserValidationText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalResumptionUserValidText);
    }

    public getLegalResumptionAfterUserValidationCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalResumptionUserValidCode);
    }

    public getLegalCheckHandoverSafetyInformationText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalHandoverSafetyInfoText);
    }

    public getLegalCheckHandoverUserDocumentationText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalHandoverUserDocText);
    }

    public getLegalCheckHandoverSafetyInformationCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalHandoverSafetyInfoCode);
    }

    public getLegalCheckHandoverUserDocumentationCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalHandoverUserDocCode);
    }

    public getDisplayConfirmSignatures(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayConfirmSignatures);
    }

    public getEnableRemoteWork(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableRemoteWork);
    }

    public getShowRemoteStatusChangeWarning(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowRemoteStatusChangeWarning);
    }

    public getDisplayDocBox(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayDocBox);
    }

    public getShowUploadToDocBoxForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowUploadToDocBoxForNotificationType, notificationType);
    }

    public getUploadToDocBoxMandatoryForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(
            ConfigConstants.configKeyUploadToDocBoxMandatoryForNotificationType,
            notificationType,
        );
    }

    public getUploadToDocBoxStatusMandatory(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUploadToDocBoxStatusMandatory);
    }

    public getUploadToDocBoxAllowedMimeTypes(): string[] {
        return this.getSplittedStrings(
            ConfigConstants.configKeyUploadToDocBoxAllowedMimeTypes,
            this.defaultAllowedMimeTypes,
        );
    }

    public getMatQuoteDistanceUnit(): string {
        return this.getStringValue(ConfigConstants.configKeyMatQuoteDistanceUnit, 'km');
    }

    public getEnableMatQuoteDistance(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableMatQuoteDistance);
    }

    public getEnableHideTimesheetAddress(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableHideTimesheetAddress);
    }

    public getHideTimesheetPersonnelSubArea(): string {
        return this.getStringValue(ConfigConstants.configKeyHideTimesheetPersonnelSubArea);
    }

    public getShowTpfReportVisibility(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowTpfReportVisibility);
    }

    public getMandatoryTpfReportVisibility(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyMandatoryTpfReportVisibility);
    }

    public getDefaultTpfReportVisibility(): number {
        return this.parseNumber(ConfigConstants.configKeyDefaultTpfReportVisibility, 2);
    }

    public getEquipmentErrorCodeRemoteWork(): string {
        return this.getStringValue(ConfigConstants.configKeyEquipmentErrorCodeRemoteWork);
    }

    public getDisplayConfirmationDistance(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayConfirmationDistance);
    }

    public getDisplayConfirmationExpenses(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayConfirmationExpenses);
    }

    public getDowntimeThresholdInMinutes(): number {
        return this.parseNumber(ConfigConstants.configKeyDowntimeThresholdInMinutes, -1);
    }

    public getEnableIndividualDocBoxDownload(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableIndividualDocBoxDownload);
    }

    public getPlaceHolderForCustomerIdentification(): string {
        return this.getStringValue(ConfigConstants.configKeyPlaceHolderForCustomerIdentification, '---');
    }

    public getShowDataMaintenanceEnhancement(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowDataMaintenanceEnhancement);
    }

    public getDisplayServicePartQuestionnaire(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayServicePartQuestionnaire);
    }

    public getMaterialOrderTextInfoLineLength(): number {
        return this.parseNumber(
            ConfigConstants.configKeyMaterialOrderTextInfoLineLength,
            ConfigConstants.defaultLineLength,
        );
    }

    public getMaterialOrderTextInfoMaxLines(): number {
        return this.parseNumber(ConfigConstants.configKeyMaterialOrderTextInfoMaxLines, Infinity);
    }

    public getMaterialOrderTextInfoPattern(): string {
        return this.getStringValue(ConfigConstants.configKeyMaterialOrderTextInfoPattern);
    }

    public getCSMLInvivoPhoneNumber(): string {
        return this.getStringValue(ConfigConstants.configKeyCSMLInvivoPhoneNumber);
    }

    public getCSMLInvitroPhoneNumber(): string {
        return this.getStringValue(ConfigConstants.configKeyCSMLInvitroPhoneNumber);
    }

    public getEnableTextInfoValidation(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableTextInfoValidation);
    }

    public getByBoxKonnectLink(): string {
        return this.getStringValue(ConfigConstants.configKeyByBoxKonnectLink);
    }

    public getCBDocLink(): string {
        return this.getStringValue(ConfigConstants.configKeyCBDocLink);
    }

    public getSeSoLink(): string {
        return this.getStringValue(ConfigConstants.configKeySeSoLink);
    }

    public getSeSoDivisions(): string[] {
        return this.getSplittedStrings(ConfigConstants.configKeySeSoDivisions);
    }

    public getEnableMatReturnWorkAround(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableMatReturnWorkAround);
    }

    public getDisplayAddMaterialButton(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayAddMaterialButton);
    }

    public getEnableBillingIndicator(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableBillingIndicator);
    }

    public getDisplayCustomerPrice(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplayCustomerPrice);
    }

    public getEnableManualIndividualization(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableManIndividualization);
    }

    public getEnableDocumentFeedbackButton(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableDocumentFeedbackButton);
    }

    public getEnableFormsHistoricalValues(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableHistoricalValues);
    }

    public getMeasurementToolsDefaultSettingsAPPS(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyMeasurementToolsDefaultSettingsAPPS);
    }

    public getHideRecoveryTimeForNotificationTypes(
        type: ConfigConstants.NotificationTypes,
        category: string[],
    ): boolean {
        const model = this.getConfigModelByKey(ConfigConstants.configKeyHideRecoveryTimeForNotificationTypes);
        const value = model?.value;
        const aTypeAndCategory = value?.split(',');

        if (aTypeAndCategory && aTypeAndCategory.length > 0) {
            return aTypeAndCategory.some((item) => {
                const trimmed = item.trim();
                if (trimmed.length === 2) {
                    return type === trimmed; // example "MA"
                } else {
                    // example "MI-IHOT"
                    const withCategory = trimmed.split('-');
                    return withCategory[0] === type && category.includes(withCategory[1]);
                }
            });
        }
        return false;
    }

    public getMaterialOrderDeliveryOptions(): string {
        return this.getStringValue(ConfigConstants.configKeyMaterialOrderDeliveryOptions);
    }

    public getMaterialReturnAddressOptions(): string {
        return this.getStringValue(ConfigConstants.configKeyMaterialReturnAddressOptions);
    }

    public getShowFCCForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowFCCForNotificationTypes, notificationType);
    }

    public getBKInfoTextForBillingIndicatorsNotReceived(): string {
        return this.getStringValue(ConfigConstants.configKeyBKInfoTextForBillingIndicatorsNotReceived);
    }

    public getShowCyberSecurityQuestion(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowCyberSecurityQuestion);
    }

    public getHideAbortAllFormsButton(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyHideAbortAllFormsButton);
    }

    public getEnableAbortFormInList(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableAbortFormInList);
    }

    /**
     * Get the max char limit for Sub address fields i.e. Department, Floor, Room for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for sub address fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestSubAddressCharLimit(): number {
        return this.parseNumber(
            ConfigConstants.configKeyMatRequestSubAddressCharLimit,
            ConfigConstants.subAddressMaxCharLimit,
        );
    }

    /**
     * Get the max char limit for contact fields i.e. Phone, Contact for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for contact fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestContactCharLimit(): number {
        return this.parseNumber(
            ConfigConstants.configKeyMatRequestContactCharLimit,
            ConfigConstants.contactMaxCharLimit,
        );
    }

    public getCommercialInfMandatoryForNonBill(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyCommercialInfMandatoryForNonBill);
    }

    public getEnableTextInfoForDop(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableTextInfoForDop);
    }

    public getHideSubmitButtonForForms(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyHideSubmitButtonForForms);
    }

    public getHideAuthorsFormsButtons(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyHideAuthorsFormsButtons);
    }

    public getShowBootOtherStockWarning(taskGroup: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyShowBootOtherStockWarning, taskGroup, true);
    }

    /**
     * Gets the LegalCheckDosageCode to display for equipment checkbox only for Country-Spain
     *
     * @returns the LegalCheckDosageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageCode(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalDosageCode);
    }

    /**
     * Gets the LegalCheckDosageText to compare with Equipment.Division only for Country-Spain
     *
     * @returns the LegalCheckDosageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageText(): string {
        return this.getStringValue(ConfigConstants.configKeyLegalDosageText);
    }

    public getEnableStatesOfUSADropdown(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableStatesOfUSADropdown);
    }

    /**
     * Gets the DeliveryOptionsEnabled to enable priority shipping logic
     *
     * @returns the DeliveryOptionsEnabled
     * @throws {Error} if service is not initialized
     */
    public getDeliveryOptionsEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDeliveryOptionsEnabled);
    }

    /**
     * Gets the DeliveryApprovalMinimumLength to get minimum length for customer approver in material order screen
     *
     * @returns the DeliveryApprovalMinimumLength
     * @throws {Error} if service is not initialized
     */
    public getDeliveryApprovalMinimumLength(): number {
        return this.parseNumber(
            ConfigConstants.configKeyDeliveryApprovalMinimumLength,
            ConfigConstants.defaultCustomerApproverLength,
        );
    }

    public isCriticalityApplicableOnNotifType(notificationType: ConfigConstants.NotificationTypes): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyCriticalityNotifTypes, notificationType);
    }

    public isCustomerDamageEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyCustomerDamageEnabled);
    }

    public isFocReplacementEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyFocReplacementEnabled);
    }

    public getEnabledAfterHoursCallOut(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableAfterHourCallOut);
    }

    public getEnableOtherTasks(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableOtherTasks);
    }

    public getOtherTasksOverlapUpcomingNoti(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyGetOtherTasksOverlapUpcomingNotification, true);
    }

    /**
     * Gets the DisplaySideMenuDocBoxFlag to enable DocBox
     * entry in the side menu
     *
     * @returns the DisplaySideMenuDocBoxFlag
     * @throws {Error} if service is not initialized
     */
    public getDisplaySideMenuDocBoxFlag(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDisplaySideMenuDocBoxFlag);
    }

    public getReportAnIssueAllowedMimeTypes(): string[] {
        return this.getSplittedStrings(
            ConfigConstants.configKeyReportAnIssueAllowedMimeTypes,
            this.defaultAllowedMimeTypes,
        );
    }

    /**
     * Countries will have the possibility to select participant's attendee (credit) type
     * between Non-credit or Credit or N/A when adding a new attendee => radio buttons
     * Current business rule is only PaceUS will have this selector
     * enabled
     *
     * @returns the boolean representing the ParticipantCreditTypeSelectorEnabled enabled
     * @throws {Error} if config service is not initialized
     */
    public getParticipantCreditTypeSelectorEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyParticipantCreditTypeSelectorEnabled);
    }

    public getEnableDurationChange(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableDurationChange);
    }

    /**
     * Gets the value for Delivery Cut off time enabled
     *
     * @returns the getEnableDeliveryCutoffTime boolean
     * @throws {Error} if service is not initialized
     */
    public getEnableDeliveryCutoffTime(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDeliveryCutoffTimeEnabled);
    }

    public getDocMissingProtocolsEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyDocMissingProtocolsEnabled);
    }

    public getEnableFDA2579Form(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableFDA2579Form);
    }

    public isSourceReplacementEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableSourceReplacement);
    }

    public getCentricarePCHEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyCentricarePCHEnabled);
    }

    public getCentricarePCHWarningTime(): number {
        return this.parseNumber(
            ConfigConstants.configKeyCentricarePCHWarningTime,
            ConfigConstants.initialCentricarePCHWarningTime,
        );
    }

    public getRadioactiveSurveyEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyRadioactiveSurveyEnabled);
    }

    public getRadioactiveSurveyDivisions(division: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyRadioactiveSurveyDivisions, division);
    }

    public getRadioactiveSurveyToolCalibration(): number | undefined {
        const model = this.getConfigModelByKey(ConfigConstants.configKeyRadioactiveSurveyToolCalibration);
        return model ? parseFloat(model.value) : undefined;
    }

    public getRadioactiveSurveyRegion(region: string): boolean {
        return this.includesConfigKey(ConfigConstants.configKeyRadioactiveSurveyRegions, region);
    }

    public getPickupRFIDEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyMaterialPickupRFIDEnabled);
    }

    public getEnableAWBPackaging(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAWBPackagingEnabled);
    }

    public getEnableFedexAwbPackagingScanning(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableFedexAwbPackaging);
    }

    public getEnableInstallationPaperwork(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyInstallationPaperworkEnabled);
    }

    public getIseCseMetricFormPowerApp(): string {
        return this.getStringValue(ConfigConstants.configKeyIseCseMetricFormAppLink);
    }

    public getEnableCommercialInfoMandatory(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableCommercialInfoMandatory);
    }

    public getShowRemoteWork(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowRemoteWork);
    }

    public getShowStartImmediatelyFollowUp(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyShowStartImmediatelyFollowUp);
    }

    public getEnablePhysicalInventory(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyPhysicalInventoryEnabled);
    }

    public getEarlyTravelHours(): number {
        return this.parseNumber(ConfigConstants.configKeyEarlyTravelHours);
    }

    public getRentalToolAllowWithNoConsumption(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyRentalToolAllowWithNoConsumption);
    }

    public getEnableBootstockReturn(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyBootstockReturnEnabled);
    }

    public getAppBookingForPartners(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAppBookingForPartners);
    }

    public getAppBookingEnabled(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAppBookingEnabled);
    }

    public getAppBookingMinDate(): number {
        return this.parseNumber(ConfigConstants.configKeyAppBookingMinDate);
    }

    public getAppBookingMaxDate(): number {
        return this.parseNumber(ConfigConstants.configKeyAppBookingMaxDate, Infinity);
    }

    public getAppBookingMaxDuration(): number {
        return this.parseNumber(ConfigConstants.configKeyAppBookingMaxDuration, Infinity);
    }

    public getAppBookingShowDedicated(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAppBookingShowDedicated);
    }

    public getAppBookingDefaultDedicated(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyAppBookingDefaultDedicated);
    }

    public getAppBookingITCS(): string {
        return this.getStringValue(ConfigConstants.configKeyAppBookingITCS);
    }

    public getEnableForceCheckin(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableForceCheckin);
    }

    public getEnableMeasurementToolsForSCRM(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableMeasurementToolsForSCRM);
    }

    public getEnableUnlimitedHandover(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableUnlimitedHandOver);
    }

    public getEnableSmartlocker(): boolean {
        return this.isSapTrue(ConfigConstants.configKeySmartlockerEnabled);
    }

    public getSmartlockerMaterialIdentifier(): string {
        return this.getStringValue(
            ConfigConstants.configKeySmartlockerIdentifier,
            ConfigConstants.defaultSmartlockerIdentifier,
        );
    }

    public getDateTimeFormatPattern(): string {
        return this.getStringValue(
            ConfigConstants.configKeyDateTimeFormatPattern,
            ConfigConstants.formatPatternDateTimeGeneric,
        );
    }

    public getUseCsmlUrlWithParam(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyUseCsmlUrlWithParam);
    }

    public isMaterialOrderUnitNonSplittable(unit: string): boolean {
        return this.hasSomeValue(ConfigConstants.configKeyMaterialOrderNonSplitUnits, unit);
    }

    public getEnableMatOrderCheckOnlineOnly(): boolean {
        return this.isSapTrue(ConfigConstants.configKeyEnableMatOrderCheckOnlineOnly);
    }
}
